/**
 * PWA service worker registration + update plumbing (CLE-57).
 *
 * Registers /sw.js (only in production builds — a SW in dev fights Vite HMR),
 * and exposes a tiny event surface so React can:
 *   - show an "Update available" prompt when a new SW is waiting, and
 *   - tell that waiting SW to activate + reload the page.
 */

type UpdateListener = (waiting: ServiceWorker) => void;

let waitingWorker: ServiceWorker | null = null;
const updateListeners = new Set<UpdateListener>();

/** Subscribe to "a new version is waiting" events. Returns an unsubscribe fn. */
export function onUpdateAvailable(listener: UpdateListener): () => void {
  updateListeners.add(listener);
  // If a worker is already waiting (registered before the listener attached),
  // notify immediately.
  if (waitingWorker) listener(waitingWorker);
  return () => updateListeners.delete(listener);
}

function notifyUpdate(worker: ServiceWorker) {
  waitingWorker = worker;
  updateListeners.forEach((l) => l(worker));
}

/** Activate the waiting SW and reload once it takes control. */
export function applyUpdate(): void {
  if (!waitingWorker) {
    window.location.reload();
    return;
  }
  // When the new SW takes control, reload so the fresh assets are used.
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
    once: true,
  });
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Don't register in dev: the SW would cache Vite's dev assets and break HMR.
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // A worker already waiting from a previous load.
        if (registration.waiting && navigator.serviceWorker.controller) {
          notifyUpdate(registration.waiting);
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // installed + an existing controller => this is an UPDATE, not the
            // first install. Surface the update prompt.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              notifyUpdate(installing);
            }
          });
        });
      })
      .catch((err) => {
        // Registration failure is non-fatal; the app still works online.
        console.warn('[pwa] service worker registration failed', err);
      });

    // Periodically check for updates (e.g. long-lived standalone sessions).
    setInterval(
      () => {
        navigator.serviceWorker.getRegistration().then((reg) => reg?.update());
      },
      60 * 60 * 1000, // hourly
    );
  });
}
